import { useState } from 'react';
import {
  Stack,
  TextInput,
  Select,
  Textarea,
  Button,
  Group,
  ActionIcon,
  Card,
  Text,
} from '@mantine/core';
import { IconPlus, IconTrash, IconSparkles, IconTools, IconBug } from '@tabler/icons-react';
import { ReleaseItemType } from '../../types/release';

type ReleaseItemInput = {
  type: ReleaseItemType;
  title: string;
  description: string;
};

type CreateReleaseFormProps = {
  onSubmit: (version: string, items: ReleaseItemInput[]) => Promise<void>;
  loading: boolean;
};

const getIconForType = (type: ReleaseItemType) => {
  switch (type) {
    case 'feature':
      return <IconSparkles size={16} color="var(--mantine-color-green-6)" />;
    case 'improvement':
      return <IconTools size={16} color="var(--mantine-color-blue-6)" />;
    case 'fix':
      return <IconBug size={16} color="var(--mantine-color-orange-6)" />;
  }
};

export const CreateReleaseForm = ({ onSubmit, loading }: CreateReleaseFormProps) => {
  const [version, setVersion] = useState('');
  const [items, setItems] = useState<ReleaseItemInput[]>([
    { type: 'feature', title: '', description: '' },
  ]);

  const addItem = () => {
    setItems([...items, { type: 'feature', title: '', description: '' }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof ReleaseItemInput, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const handleSubmit = async () => {
    // Validation
    if (!version.trim()) {
      return;
    }

    const validItems = items.filter(item => item.title.trim() && item.description.trim());
    if (validItems.length === 0) {
      return;
    }

    await onSubmit(version.trim(), validItems);

    // Reset form
    setVersion('');
    setItems([{ type: 'feature', title: '', description: '' }]);
  };

  const isValid = version.trim() && items.some(item => item.title.trim() && item.description.trim());

  return (
    <Stack gap="md">
      <TextInput
        label="Version"
        placeholder="e.g., 2025.1.1"
        value={version}
        onChange={(e) => setVersion(e.target.value)}
        required
      />

      <Stack gap="sm">
        <Text fw={500} size="sm">
          Release Items
        </Text>

        {items.map((item, index) => (
          <Card key={index} withBorder p="sm" radius="lg">
            <Stack gap="xs">
              <Group justify="space-between" align="flex-start">
                <Select
                  label="Type"
                  value={item.type}
                  onChange={(value) => updateItem(index, 'type', value as ReleaseItemType)}
                  data={[
                    { value: 'feature', label: 'Feature' },
                    { value: 'improvement', label: 'Improvement' },
                    { value: 'fix', label: 'Fix' },
                  ]}
                  leftSection={getIconForType(item.type)}
                  style={{ flex: 1 }}
                  required
                />
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => removeItem(index)}
                  disabled={items.length === 1}
                  mt="xl"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>

              <TextInput
                label="Title"
                placeholder="e.g., Quick Bites"
                value={item.title}
                onChange={(e) => updateItem(index, 'title', e.target.value)}
                required
              />

              <Textarea
                label="Description"
                placeholder="e.g., Create 30-minute recipes using ingredients on hand"
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                minRows={2}
                required
              />
            </Stack>
          </Card>
        ))}

        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={addItem}
        >
          Add Item
        </Button>
      </Stack>

      <Button
        onClick={handleSubmit}
        loading={loading}
        disabled={!isValid || loading}
      >
        Create Release
      </Button>
    </Stack>
  );
};
